import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/queries/keys';
import askHobbesHistoryService from '@/lib/api/askHobbesHistory';
import type { AskHobbesHistoryListResponse } from '@/lib/api/askHobbesHistory';

/**
 * React Query hook to fetch paginated Ask Hobbes conversation history.
 */
export function useAskHobbesHistory(organizationId: string | undefined) {
  return useQuery<AskHobbesHistoryListResponse>({
    queryKey: queryKeys.askHobbesHistory.list(organizationId ?? ''),
    queryFn: async () => {
      const res = await askHobbesHistoryService.getHistory(organizationId!);
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to fetch Ask Hobbes history');
      }
      return res.data;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

/**
 * Mutation hook to delete a single Ask Hobbes history entry.
 */
export function useDeleteAskHobbesEntry(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const res = await askHobbesHistoryService.deleteEntry(entryId);
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to delete history entry');
      }
      return res.data;
    },
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.askHobbesHistory.list(organizationId),
        });
      }
    },
  });
}
